import Axios from 'axios';
import { Branch } from './branchApi';
import { Client } from './clientsApi';

export enum OtherExpenseType {
  PARKING = 'PARKING',
  DISPOSAL = 'DISPOSAL',
  SPECIALIZED_TRADE = 'SPECIALIZED_TRADE',
  OTHER = 'OTHER',
  // One of the organization's own categories, named by otherExpenseCategoryId.
  CUSTOM = 'CUSTOM',
}

export type OtherExpenseCategory = {
  id: string;
  name: string;
  isArchived: boolean;
};

export type OtherExpensesMargin = {
  id: string;
  organizationId: number;
  otherExpenseType: OtherExpenseType;
  otherExpenseCategoryId?: string | null;
  otherExpenseCategory?: OtherExpenseCategory | null;
  margin: number;
  effectiveFrom: string;
  clientId?: string;
  client?: Client;
  branchId?: string;
  branch?: Branch;
  isDefault: boolean;
  createdOn: string;
  updatedOn: string;
  createdByUserId: string;
  updatedByUserId: string;
};

export type OtherExpenseRateFormType = {
  otherExpenseType: OtherExpenseType;
  otherExpenseCategoryId?: string | null;
  margin: number;
  clientId?: string;
  branchId?: string;
  isDefault: boolean;
  effectiveFrom: string;
  branch?: Branch;
  client?: Client;
  removableItemId?: string;
};

export async function addOtherExpensesMargin({
  data,
}: {
  data: OtherExpenseRateFormType;
}) {
  const res = await Axios.post(`/api/v1/other-expenses-margin`, data);
  return res.data;
}

export async function getOtherExpenseMargins(): Promise<OtherExpensesMargin[]> {
  const res = await Axios.get(`/api/v1/other-expenses-margin`);

  return res.data;
}

export async function deleteOtherExpensesMargin(id: string) {
  try {
    const response = await Axios.delete(`/api/v1/other-expenses-margin/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete other expenses margin: ${error}`);
  }
}

export async function updateOtherExpensesMargin({
  id,
  data,
}: {
  id: string;
  data: Partial<OtherExpenseRateFormType>;
}) {
  const res = await Axios.put(`/api/v1/other-expenses-margin/${id}`, data);
  return res.data;
}

// The four default categories, in the order they are shown. Custom categories
// go between Specialised Trade and Other.
// !! Important: use "Specialised" (AU/UK) on the UI instead of "Specialized" (US).
export const DEFAULT_OTHER_EXPENSE_LABELS: Record<
  Exclude<OtherExpenseType, OtherExpenseType.CUSTOM>,
  string
> = {
  [OtherExpenseType.PARKING]: 'Parking',
  [OtherExpenseType.DISPOSAL]: 'Disposal',
  [OtherExpenseType.SPECIALIZED_TRADE]: 'Specialised Trade',
  [OtherExpenseType.OTHER]: 'Other',
};

export const CUSTOM_CATEGORY_NAME_MAX_LENGTH = 50;

export function sortCategoriesByName<T extends Pick<OtherExpenseCategory, 'name'>>(
  categories: T[]
): T[] {
  return [...categories].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

// Custom categories (ADR-0003 in the backend repo)
export async function getOtherExpenseCategories({
  includeArchived = false,
}: { includeArchived?: boolean } = {}): Promise<OtherExpenseCategory[]> {
  const res = await Axios.get(`/api/v1/other-expense-category`, {
    params: includeArchived ? { includeArchived: true } : undefined,
  });
  return res.data;
}

export async function createOtherExpenseCategory(
  name: string
): Promise<OtherExpenseCategory> {
  const res = await Axios.post(`/api/v1/other-expense-category`, { name });
  return res.data;
}

export async function renameOtherExpenseCategory({
  id,
  name,
}: {
  id: string;
  name: string;
}): Promise<OtherExpenseCategory> {
  const res = await Axios.put(`/api/v1/other-expense-category/${id}`, { name });
  return res.data;
}

export async function setOtherExpenseCategoryArchived({
  id,
  isArchived,
}: {
  id: string;
  isArchived: boolean;
}): Promise<OtherExpenseCategory> {
  const action = isArchived ? 'archive' : 'restore';
  const res = await Axios.put(`/api/v1/other-expense-category/${id}/${action}`);
  return res.data;
}

/**
 * One string naming any category, default or custom, for select values, row
 * keys and filters: the type for a default ("PARKING"), "CUSTOM:<id>" for a
 * custom one.
 */
export function otherExpenseCategoryKey(
  otherExpenseType: OtherExpenseType,
  otherExpenseCategoryId?: string | null
): string {
  return otherExpenseType === OtherExpenseType.CUSTOM
    ? `${OtherExpenseType.CUSTOM}:${otherExpenseCategoryId}`
    : otherExpenseType;
}

export function parseOtherExpenseCategoryKey(key: string): {
  otherExpenseType: OtherExpenseType;
  otherExpenseCategoryId: string | null;
} {
  const customPrefix = `${OtherExpenseType.CUSTOM}:`;
  if (key.startsWith(customPrefix)) {
    return {
      otherExpenseType: OtherExpenseType.CUSTOM,
      otherExpenseCategoryId: key.slice(customPrefix.length),
    };
  }
  return {
    otherExpenseType: key as OtherExpenseType,
    otherExpenseCategoryId: null,
  };
}

export type OtherExpenseCategoryOption = {
  key: string;
  label: string;
  otherExpenseType: OtherExpenseType;
  // Set for a custom category.
  category?: OtherExpenseCategory;
};

/**
 * Every category in display order: Parking, Disposal, Specialised Trade, the
 * custom categories A–Z, then Other last as the catch-all.
 */
export function buildOtherExpenseCategoryOptions(
  customCategories: OtherExpenseCategory[]
): OtherExpenseCategoryOption[] {
  const defaultOption = (
    otherExpenseType: Exclude<OtherExpenseType, OtherExpenseType.CUSTOM>
  ): OtherExpenseCategoryOption => ({
    key: otherExpenseType,
    label: DEFAULT_OTHER_EXPENSE_LABELS[otherExpenseType],
    otherExpenseType,
  });

  return [
    defaultOption(OtherExpenseType.PARKING),
    defaultOption(OtherExpenseType.DISPOSAL),
    defaultOption(OtherExpenseType.SPECIALIZED_TRADE),
    ...sortCategoriesByName(customCategories).map((category) => ({
      key: otherExpenseCategoryKey(OtherExpenseType.CUSTOM, category.id),
      label: category.name,
      otherExpenseType: OtherExpenseType.CUSTOM,
      category,
    })),
    defaultOption(OtherExpenseType.OTHER),
  ];
}
